/**
 * ``useDynamicTrack`` — read + write the per-user dynamic-track tail
 * config, backed by the working-state ``map.platform.dynamic_track``
 * Profile-document slice (ADR-033 §9.3; relocated from the v2
 * ``map_settings.dynamic_track`` per the preferences-v3 flip, issue
 * #278 → ADR-033).
 *
 * The map settings modal uses both ``config`` (to populate the toggle +
 * km field) and ``setConfig`` (to persist edits via the existing
 * PUT /api/me/preferences round-trip). The :class:`TrackTailLayer` uses
 * ``config`` to gate rendering (``enabled``) and trim each tail to
 * ``length_km``. Defaults (enabled / 10 km) apply when the slice is
 * absent — matching the BFF ``DynamicTrackSettings`` defaults.
 *
 * Cross-references:
 *   - REQ-AUTH-053 / 070 (preferences persistence; v3 working_state).
 *   - ADR-033 §9.3 (``map.platform.dynamic_track`` slice).
 */
import { usePreferences } from "@/preferences/preferences-context";
import type { SetPreferencesResult } from "@/preferences/preferences-context";

/** Default tail config — mirrors the BFF ``DynamicTrackSettings`` defaults. */
export const DYNAMIC_TRACK_DEFAULTS: DynamicTrackConfig = {
  enabled: true,
  length_km: 10,
};

/** Friendly UI bounds for the km stepper (the BFF validates (0, 1000]). */
export const DYNAMIC_TRACK_MIN_KM = 1;
export const DYNAMIC_TRACK_MAX_KM = 100;
export const DYNAMIC_TRACK_STEP_KM = 1;

/** Resolved (non-optional) dynamic-track config the UI works with. */
export interface DynamicTrackConfig {
  enabled: boolean;
  length_km: number;
}

export interface UseDynamicTrackResult {
  /** Current config, with defaults applied for any absent field. */
  config: DynamicTrackConfig;
  /** Merge a patch into the config and persist it (PUT /api/me/preferences). */
  setConfig: (patch: Partial<DynamicTrackConfig>) => Promise<SetPreferencesResult>;
}

export function useDynamicTrack(): UseDynamicTrackResult {
  const { workingState, setWorkingState } = usePreferences();
  const platform = workingState.map?.platform;
  const stored = platform?.dynamic_track;
  const config: DynamicTrackConfig = {
    enabled: stored?.enabled ?? DYNAMIC_TRACK_DEFAULTS.enabled,
    length_km: stored?.length_km ?? DYNAMIC_TRACK_DEFAULTS.length_km,
  };

  const setConfig = (patch: Partial<DynamicTrackConfig>): Promise<SetPreferencesResult> => {
    const next: DynamicTrackConfig = { ...config, ...patch };
    // Round-trip the rest of ``map`` (and ``map.platform``) so this
    // edit doesn't drop another slice's data — the working-state
    // setter shallow-merges only the top-level ``map`` key.
    //
    // ``PlatformMapSettings`` requires ``color_mode`` + ``single_color``
    // (the BFF populates both on read, so ``platform`` carries them in
    // practice). The defaults here cover the degenerate no-``platform``
    // case (a default working state with no map slice yet), matching
    // the BFF schema defaults (``by_status`` / ``#3ecf8e``).
    return setWorkingState({
      map: {
        ...workingState.map,
        platform: {
          color_mode: "by_status",
          single_color: "#3ecf8e",
          glyph: "triangle",
          ...platform,
          dynamic_track: next,
        },
      },
    });
  };

  return { config, setConfig };
}
